import { Inbox, TriangleAlert } from 'lucide-react';
import type { ReactNode } from 'react';
import { useT } from '@/lib/i18n';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { Spinner } from './spinner';

/** Shared status surfaces. Never surface a raw error message to the user. */

export function LoadingState({ label }: { label?: string }) {
  const t = useT();
  return (
    <div className="flex items-center justify-center gap-2 py-10 text-fg-subtle sm:py-16">
      <Spinner />
      <span className="text-body">{label ?? t.loading()}</span>
    </div>
  );
}

/** Pulsing placeholder block. Compose into layout-shaped loading states. */
export function Skeleton({ className }: { className?: string }) {
  return <div aria-hidden className={cn('animate-pulse rounded-md bg-surface-3', className)} />;
}

/** Card-shaped table placeholder — mirrors the data table's footprint while loading. */
export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div
      aria-busy="true"
      className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4 shadow-card"
    >
      {Array.from({ length: rows }, (_, i) => `sk-${i}`).map((k) => (
        <Skeleton key={k} className="h-5" />
      ))}
    </div>
  );
}

export function ErrorState({ onRetry }: { onRetry?: () => void }) {
  const t = useT();
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-10 text-center sm:py-16">
      <TriangleAlert className="size-6 text-warn" aria-hidden />
      <div>
        <p className="text-section text-fg">{t.errorTitle()}</p>
        <p className="mt-1 text-body text-fg-muted">{t.errorBody()}</p>
      </div>
      {onRetry ? (
        <Button variant="secondary" size="sm" onClick={onRetry}>
          {t.retry()}
        </Button>
      ) : null}
    </div>
  );
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-8 text-center sm:py-12">
      <Inbox className="size-6 text-fg-subtle" aria-hidden />
      <p className="text-section text-fg">{title}</p>
      {children ? (
        <div className="w-full max-w-md px-4 text-body text-fg-muted">{children}</div>
      ) : null}
    </div>
  );
}
